import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import type { AvailabilityMap, AvailabilityStatus, Contributor, ContributorWeekSchedule, Solution, WeekSchedulePayload } from '../types';
import { StatusToggle } from './StatusToggle';
import { resolveEffectiveStatus } from '../utils/availabilityResolver';
import { isLongLeaveRange } from '../utils/longLeave';
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function getDatesForWeek(weekStart: string): string[] {
  const dates: string[] = [];
  const start = new Date(weekStart + 'T00:00:00Z');
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}
function cycleStatus(current: AvailabilityStatus): AvailabilityStatus {
  const cycle: AvailabilityStatus[] = ['available', 'limited', 'unavailable'];
  return cycle[(cycle.indexOf(current) + 1) % cycle.length];
}

// Mirrors the OOO check in AvailabilityGrid but for any date, not just today.
function isDateOOO(date: string, contributor: Contributor): boolean {
  if (contributor.oooStartDate && contributor.oooEndDate) {
    return contributor.oooStartDate <= date && contributor.oooEndDate >= date;
  }
  return !!contributor.isOutOfOffice;
}
interface Props {
  contributor: Contributor;
  solutions: Solution[];
  baseAvailability: AvailabilityMap;
  canEdit: boolean;
  onClose: () => void;
  fetchCurrentWeek: (contributorId: string) => Promise<WeekSchedulePayload | null>;
  fetchNextWeek: (contributorId: string) => Promise<WeekSchedulePayload | null>;
  onUpdateDailyAvailability: (solutionId: string, date: string, status: AvailabilityStatus | null) => Promise<void>;
  /** true (default) = two-tab slide-over panel; false = single 14-column inline grid */
  showTabs?: boolean;
}
export function ContributorWeekView({
  contributor,
  solutions,
  baseAvailability,
  canEdit,
  onClose,
  fetchCurrentWeek,
  fetchNextWeek,
  onUpdateDailyAvailability,
  showTabs = true
}: Props) {
  const [currentWeek, setCurrentWeek] = useState<WeekSchedulePayload | null>(null);
  const [nextWeek, setNextWeek] = useState<WeekSchedulePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [markingSolutions, setMarkingSolutions] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'current' | 'next'>('current');
  const today = new Date().toISOString().split('T')[0];
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCurrentWeek(contributor.id), fetchNextWeek(contributor.id)]).then(([cw, nw]) => {
      setCurrentWeek(cw);
      setNextWeek(nw);
      setLoading(false);
    });
  }, [contributor.id]);
  const activeWeek = activeTab === 'current' ? currentWeek : nextWeek;
  const setActiveWeek = activeTab === 'current' ? setCurrentWeek : setNextWeek;
  const handleCellClick = async (weekPayload: WeekSchedulePayload, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>, solutionId: string, date: string) => {
    if (!canEdit) return;
    const key = `${date}_${solutionId}`;
    setUpdating(key);
    const currentStatus = resolveEffectiveStatus(contributor.id, solutionId, {
      [contributor.id]: weekPayload.schedule[date] ?? {}
    }, baseAvailability) ?? 'unavailable';
    const newStatus = cycleStatus(currentStatus);
    setWeekFn(prev => {
      if (!prev) return prev;
      const updatedSchedule: ContributorWeekSchedule = {
        ...prev.schedule,
        [date]: {
          ...(prev.schedule[date] ?? {}),
          [solutionId]: newStatus
        }
      };
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    await onUpdateDailyAvailability(solutionId, date, newStatus);
    setUpdating(null);
  };

  // Bulk-sets all 7 days of a single solution's row to `status` for the given week.
  const handleMarkSolutionWeek = async (weekPayload: WeekSchedulePayload, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>, solutionId: string, status: AvailabilityStatus) => {
    if (!canEdit) return;
    const markKey = `${weekPayload.weekKey}_${solutionId}`;
    if (markingSolutions[markKey]) return;
    setMarkingSolutions(prev => ({
      ...prev,
      [markKey]: true
    }));

    // Only weekdays (Mon-Fri) — Sat/Sun are excluded from bulk week updates.
    const dates = getDatesForWeek(weekPayload.weekStart).slice(0, 5);
    setWeekFn(prev => {
      if (!prev) return prev;
      const updatedSchedule: ContributorWeekSchedule = {
        ...prev.schedule
      };
      for (const date of dates) {
        updatedSchedule[date] = {
          ...(updatedSchedule[date] ?? {}),
          [solutionId]: status
        };
      }
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    try {
      await Promise.all(dates.map(date => onUpdateDailyAvailability(solutionId, date, status)));
    } finally {
      setMarkingSolutions(prev => {
        const next = {
          ...prev
        };
        delete next[markKey];
        return next;
      });
    }
  };

  // Clears any per-day weekly overrides across all 7 days of the given week,
  // restoring each day to whatever the base/date-range resolution would show.
  const handleClearSolutionWeek = async (weekPayload: WeekSchedulePayload, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>, solutionId: string) => {
    if (!canEdit) return;
    const markKey = `${weekPayload.weekKey}_${solutionId}`;
    if (markingSolutions[markKey]) return;
    setMarkingSolutions(prev => ({
      ...prev,
      [markKey]: true
    }));
    const dates = getDatesForWeek(weekPayload.weekStart);
    setWeekFn(prev => {
      if (!prev) return prev;
      const updatedSchedule: ContributorWeekSchedule = {
        ...prev.schedule
      };
      for (const date of dates) {
        if (!updatedSchedule[date] || updatedSchedule[date][solutionId] === undefined) continue;
        const {
          [solutionId]: _removed,
          ...rest
        } = updatedSchedule[date];
        updatedSchedule[date] = rest;
      }
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    try {
      await Promise.all(dates.map(date => onUpdateDailyAvailability(solutionId, date, null)));
    } finally {
      setMarkingSolutions(prev => {
        const next = {
          ...prev
        };
        delete next[markKey];
        return next;
      });
    }
  };

  // Used by the tabbed view (slide-over panel)
  const renderWeekGrid = (weekPayload: WeekSchedulePayload | null, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>) => {
    if (!weekPayload) {
      return <div className="p-4 text-slate-500 text-sm" data-id="element-53" data-mp="2mi77lseay4qq">Failed to load week schedule.</div>;
    }
    const dates = getDatesForWeek(weekPayload.weekStart);
    return <table className="w-full text-xs border-collapse" data-id="element-54" data-mp="2hpw46fkcs0tf">
        <thead data-id="element-55" data-mp="2p359hhjzk50q">
          <tr data-id="element-56" data-mp="2p3nassli99fi">
            <th className="text-left py-2 px-3 text-slate-500 font-medium w-48 min-w-[160px]" data-id="element-57" data-mp="2p46hte9mader">
              Solution
            </th>
            {dates.map((date, i) => {
            const isToday = date === today;
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            return <th key={date} className={`text-center py-2 px-2 font-medium w-16 ${isOOO ? 'bg-slate-100 text-slate-400' : isToday ? 'text-[#EB1000]' : isWeekend ? 'bg-slate-100 text-slate-400' : 'text-slate-500'}`} data-id="element-58" data-mp="381hjt6mmvkxw" data-mpi={i}>
                  <div data-id="element-59" data-mp="3qta8wpyl0m0c">{DAY_LABELS[i]}</div>
                  <div className={`text-[10px] font-normal mt-0.5 ${isToday && !isOOO ? 'font-semibold' : ''}`} data-id="element-60" data-mp="3qq4nbkp5w29d">
                    {date.slice(5)}
                  </div>
                  {isToday && !isOOO && <div className="mx-auto mt-1 h-1 w-1 rounded-full bg-[#EB1000]" data-id="element-61" data-mp="kyftk5hbho5m" />}
                  {isOOO && <div className={`text-[9px] mt-0.5 font-medium ${isLongLeaveRange(contributor.oooStartDate, contributor.oooEndDate) ? 'text-purple-600' : ''}`} data-id="element-62" data-mp="uuitd4op5ape">
                      {isLongLeaveRange(contributor.oooStartDate, contributor.oooEndDate) ? 'LONG LEAVE' : 'OOO'}
                    </div>}
                </th>;
          })}
          </tr>
        </thead>
        <tbody data-id="element-63" data-mp="bvoezwt5ac9r">
          {solutions.map(solution => <tr key={solution.id} className="group border-t border-slate-100 hover:bg-slate-50/50 transition-colors" data-id="element-64" data-mp="bx99jmfglv7m">
              <td className="py-2 px-3 font-medium max-w-[160px]" data-id="element-65" data-mp="73dbyl33yacz">
                <div className="flex flex-col gap-0.5" data-id="element-66" data-mp="3fjgksu5yi52w">
                  <span className="text-slate-700 truncate" data-id="element-67" data-mp="3amgfwmkuf8dd">{solution.name}</span>
                  {canEdit && <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1" data-id="element-68" data-mp="1q3mh5jx6760t">
                      {markingSolutions[`${weekPayload.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-69" data-mp="1g6rhgxp5uzky" /> : <>
                          {(['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(weekPayload, setWeekFn, solution.id, s)} title={`Mark whole week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-70" data-mp="28sayxx0cwv8d" />)}
                          <button onClick={() => handleClearSolutionWeek(weekPayload, setWeekFn, solution.id)} title="Clear weekly overrides" className="h-3.5 w-3.5 rounded-full flex-shrink-0 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-id="element-71" data-mp="1nzt0jdyvxq4t">
                            <X className="h-3 w-3" data-id="element-72" data-mp="154ughyrmmsyq" />
                          </button>
                        </>}
                    </div>}
                </div>
              </td>
              {dates.map((date, i) => {
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = weekPayload.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(contributor.id, solution.id, weeklyScheduleForDate, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            const hasWeeklyOverride = weeklyOverride !== undefined;
            return <td key={date} className={`py-2 px-2 text-center ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-100' : ''}`} data-id="element-73" data-mp="16ow2frl7iohz" data-mpi={i}>
                    <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : isWeekend ? 'opacity-60' : ''}`} data-id="element-74" data-mp="11qktdj2qki0t">
                      {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-75" data-mp="33h62oxb3ws6l" /> : <StatusToggle status={status} onClick={() => handleCellClick(weekPayload, setWeekFn, solution.id, date)} disabled={!canEdit || isOOO || isWeekend} data-id="element-76" data-mp="17ejeiy92tqdz" />}
                      {hasWeeklyOverride && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-77" data-mp="1qcn9mos4pows" />}
                    </div>
                  </td>;
          })}
            </tr>)}
        </tbody>
      </table>;
  };

  // Used by the inline grid view (name click) — all 14 dates in one horizontally-scrollable table
  const renderCombinedGrid = (cw: WeekSchedulePayload | null, nw: WeekSchedulePayload | null) => {
    const currentDates = cw ? getDatesForWeek(cw.weekStart) : [];
    const nextDates = nw ? getDatesForWeek(nw.weekStart) : [];
    const renderDateTh = (date: string, i: number, isFirstOfNextWeek: boolean) => {
      const isToday = date === today;
      const isWeekend = i >= 5;
      const isOOO = isDateOOO(date, contributor);
      return <th key={date} className={`text-center py-2 px-1 font-medium ${isFirstOfNextWeek ? 'border-l-2 border-slate-300' : ''} ${isOOO ? 'bg-slate-100 text-slate-400' : isToday ? 'text-[#EB1000]' : isWeekend ? 'bg-slate-100 text-slate-400' : 'text-slate-500'}`} data-id="element-78" data-mp="3kl98t2al1elq">
          <div data-id="element-79" data-mp="7dfmwfhewi0p">{DAY_LABELS[i]}</div>
          <div className={`text-[10px] font-normal mt-0.5 ${isToday && !isOOO ? 'font-semibold' : ''}`} data-id="element-80" data-mp="14t3bpz5scnpq">
            {date.slice(5)}
          </div>
          {isToday && !isOOO && <div className="mx-auto mt-1 h-1 w-1 rounded-full bg-[#EB1000]" data-id="element-81" data-mp="100a4n4muh3ah" />}
          {isOOO && <div className="text-[9px] mt-0.5 font-medium" data-id="element-82" data-mp="jlasrhovje6z">OOO</div>}
        </th>;
    };
    return <table className="w-full table-fixed text-xs border-collapse" data-id="element-83" data-mp="on8hgrg1n17b">
          <thead data-id="element-84" data-mp="anh7d1sne4yl">
            {/* Week group labels */}
            <tr data-id="element-85" data-mp="fl8uzjm9f4wz">
              <th className="py-1.5 px-3" style={{
            width: '160px'
          }} data-id="element-86" data-mp="fkqta7gtvyho" />
              <th colSpan={7} className="text-center py-1.5 text-[11px] font-semibold text-slate-600 border-b border-slate-100" data-id="element-87" data-mp="fn3hv3h3d73p">
                This Week
                {cw && <span className="ml-1.5 font-normal text-slate-400" data-id="element-88" data-mp="tlthoqvapkvr">
                    {cw.weekStart.slice(5)} – {cw.weekEnd.slice(5)}
                  </span>}
              </th>
              <th colSpan={7} className="text-center py-1.5 text-[11px] font-semibold text-slate-600 border-b border-slate-100 border-l-2 border-l-slate-300" data-id="element-89" data-mp="1m9fk3vssejh1">
                Next Week
                {nw && <span className="ml-1.5 font-normal text-slate-400" data-id="element-90" data-mp="20cv0lxkytb6h">
                    {nw.weekStart.slice(5)} – {nw.weekEnd.slice(5)}
                  </span>}
              </th>
            </tr>
            {/* Day headers */}
            <tr data-id="element-91" data-mp="255o7os3wovlq">
              <th className="text-left py-2 px-3 text-slate-500 font-medium" style={{
            width: '160px'
          }} data-id="element-92" data-mp="257930jy1oikn">
                Solution
              </th>
              {currentDates.map((date, i) => renderDateTh(date, i, false))}
              {nextDates.map((date, i) => renderDateTh(date, i, i === 0))}
            </tr>
          </thead>
          <tbody data-id="element-93" data-mp="1rc6m71rnt8t2">
            {solutions.map(solution => <tr key={solution.id} className="group border-t border-slate-100 hover:bg-slate-50/50 transition-colors" data-id="element-94" data-mp="1meone47cw9ys">
                <td className="py-2 px-3 font-medium" style={{
            width: '160px'
          }} data-id="element-95" data-mp="32rvbhzsdyjx1">
                  <div className="flex flex-col gap-0.5" data-id="element-96" data-mp="2xu3if2g08rr9">
                    <span className="text-slate-700 truncate" data-id="element-97" data-mp="35vucve9im23t">{solution.name}</span>
                    {canEdit && <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-0.5" data-id="element-98" data-mp="39vv2zw5z81qp">
                        {cw && <div className="flex items-center gap-0.5" data-id="element-99" data-mp="3eu6dm943ig9z">
                            <span className="text-[9px] text-slate-400 shrink-0 mr-0.5" data-id="element-100" data-mp="3jukbwzfwabut">CW:</span>
                            {markingSolutions[`${cw.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-101" data-mp="2r7g8v00d47lo" /> : <>
                                {(['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(cw, setCurrentWeek, solution.id, s)} title={`Mark current week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-102" data-mp="23hc953qaorzy" />)}
                                <button onClick={() => handleClearSolutionWeek(cw, setCurrentWeek, solution.id)} title="Clear current week overrides" className="h-3.5 w-3.5 rounded-full flex-shrink-0 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-id="element-103" data-mp="1d4qojjxi6myr">
                                  <X className="h-3 w-3" data-id="element-104" data-mp="2i444elfrgfht" />
                                </button>
                              </>}
                          </div>}
                        {nw && <div className="flex items-center gap-0.5" data-id="element-105" data-mp="2867v7z6dvumc">
                            <span className="text-[9px] text-slate-400 shrink-0 mr-0.5" data-id="element-106" data-mp="2d4j5uc4i695m">NW:</span>
                            {markingSolutions[`${nw.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-107" data-mp="2454elagihuk2" /> : <>
                                {(['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(nw, setNextWeek, solution.id, s)} title={`Mark next week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-108" data-mp="1gfa3axbuce1x" />)}
                                <button onClick={() => handleClearSolutionWeek(nw, setNextWeek, solution.id)} title="Clear next week overrides" className="h-3.5 w-3.5 rounded-full flex-shrink-0 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-id="element-109" data-mp="1frkqvypge7pl">
                                  <X className="h-3 w-3" data-id="element-110" data-mp="1yh0uww0ckiar" />
                                </button>
                              </>}
                          </div>}
                      </div>}
                  </div>
                </td>
                {/* Current week cells */}
                {currentDates.map((date, i) => {
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = cw?.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(contributor.id, solution.id, weeklyScheduleForDate, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            return <td key={date} className={`py-2 px-2 text-center ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-100' : ''}`} data-id="element-111" data-mp="3kzlaan79eh6u" data-mpi={i}>
                      <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : isWeekend ? 'opacity-60' : ''}`} data-id="element-112" data-mp="36bgqth0du1n0">
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-113" data-mp="2u8ch74v5jx1" /> : <StatusToggle status={status} onClick={() => cw && handleCellClick(cw, setCurrentWeek, solution.id, date)} disabled={!canEdit || !cw || isOOO || isWeekend} data-id="element-114" data-mp="7tbfyn573i2w" />}
                        {weeklyOverride !== undefined && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-115" data-mp="2ilcvry19fa7m" />}
                      </div>
                    </td>;
          })}
                {/* Next week cells */}
                {nextDates.map((date, i) => {
            const isWeekend = i >= 5;
            const isFirstCell = i === 0;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = nw?.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(contributor.id, solution.id, weeklyScheduleForDate, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            return <td key={date} className={`py-2 px-2 text-center ${isFirstCell ? 'border-l-2 border-slate-300' : ''} ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-100' : ''}`} data-id="element-116" data-mp="1u9x5p6rmrk3e" data-mpi={i}>
                      <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : isWeekend ? 'opacity-60' : ''}`} data-id="element-117" data-mp="232qxg3o9kby5">
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-118" data-mp="1bc4bcsv2oihm" /> : <StatusToggle status={status} onClick={() => nw && handleCellClick(nw, setNextWeek, solution.id, date)} disabled={!canEdit || !nw || isOOO || isWeekend} data-id="element-119" data-mp="1fcwj1hoaqfbs" />}
                        {weeklyOverride !== undefined && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-120" data-mp="30nwcy56pvh9s" />}
                      </div>
                    </td>;
          })}
              </tr>)}
          </tbody>
        </table>;
  };
  const legend = <p className="mt-3 text-xs text-slate-400" data-id="element-121" data-mp="hivavesrwom1">
      <span className="inline-flex items-center gap-1" data-id="element-122" data-mp="hi3psxwsy9a0">
        <span className="h-1.5 w-1.5 rounded-full bg-blue-400 inline-block" data-id="element-123" data-mp="wg5s74s3kopo" />
        Weekly override set &nbsp;·&nbsp; Click a circle to cycle: available → limited → unavailable
      </span>
    </p>;
  if (loading) {
    return <div className="flex items-center justify-center p-12" data-id="element-124" data-mp="157ejjvfqj3en">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" data-id="element-125" data-mp="1174dyjvuuf0b" />
        <span className="ml-2 text-slate-500 text-sm" data-id="element-126" data-mp="117ntxv1y2tbp">Loading week schedule…</span>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-127" data-mp="1f4asg29jprjq">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white" data-id="element-128" data-mp="1a93w5filvp4u">
        <div data-id="element-129" data-mp="1ttvopp3wsncz">
          <h2 className="text-base font-semibold text-slate-900" data-id="element-130" data-mp="1tr9jhw4hqjxx">{contributor.name}</h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-131" data-mp="1yrnt3ilqq1y2">Weekly availability — {contributor.region}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Close week view" data-id="element-132" data-mp="1jx9513nbk2cj">
          <X className="h-4 w-4" data-id="element-133" data-mp="1b107juiwjdt1" />
        </button>
      </div>

      {showTabs ? <>
          {/* Week tabs */}
          <div className="flex border-b border-slate-200 bg-white px-6" data-id="element-134" data-mp="114x2ogwo112e">
            {(['current', 'next'] as const).map(tab => {
          const week = tab === 'current' ? currentWeek : nextWeek;
          const label = tab === 'current' ? 'This Week' : 'Next Week';
          const range = week ? `${week.weekStart.slice(5)} – ${week.weekEnd.slice(5)}` : '';
          return <button key={tab} onClick={() => setActiveTab(tab)} className={`mr-6 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-[#EB1000] text-[#EB1000]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-135" data-mp="2yt11cyck854d">
                  {label}
                  {range && <span className="ml-1.5 text-xs font-normal opacity-70" data-id="element-136" data-mp="3cs0mhw0v61vs">{range}</span>}
                </button>;
        })}
          </div>
          <div className="overflow-auto flex-1 p-4" data-id="element-137" data-mp="37rcouri4qmsl">
            {activeWeek ? renderWeekGrid(activeWeek, setActiveWeek) : <div className="p-4 text-slate-500 text-sm" data-id="element-138" data-mp="1wdsf58onzz4q">Failed to load week schedule.</div>}
            {legend}
          </div>
        </> : (/* Single 14-column pane — horizontally scrollable */
    <div className="overflow-auto flex-1 p-4" data-id="element-139" data-mp="7ued0kptrwxl">
          {renderCombinedGrid(currentWeek, nextWeek)}
          {legend}
        </div>)}
    </div>;
}